import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Layout from "app/layout/Layout";
import colors from "app/styles/colors";
import BreadCrumbs from "app/components/Breadcrumbs/Breadcrumbs";
import {
  DeleteOutlineTwoTone,
  EditTwoTone,
  ExpandMore,
} from "@mui/icons-material";
import { PrimaryButton, SecondaryButton } from "app/components/Buttons/Button";
import { LoadingCircle } from "app/components/UI/Loader";
import WhyPTNDialog from "app/components/Dialog/WhyPTNDialog";
import ImageEditorCombine from "app/components/Editors/ImageEditorCombine";
import TextEditorCombine from "app/components/Editors/TextEditorCombine";
import { checkCharacterValidation, checkImageValidation } from "app/utils";
import FleetManagementServices from "app/apis/FleetManagement/FleetManagementServices";
import { ErrorToaster, SuccessToaster } from "app/components/Toaster/Toaster";

let characterLength = {
  subContent: 870,
  topContent: 460,
};

function FleetManagement() {
  const [showDialog, setShowDialog] = useState(false);
  const [allImages, setAllImages] = useState({});
  const [allContents, setAllContents] = useState({});
  const [itemData, setItemData] = useState({});
  const [loader, setLoader] = useState(true);
  const [ptnCardList, setPtnCardList] = useState([]);
  const [selectedItem, setSelectedItem] = useState({});

  const handleDataSubmission = async () => {
    try {
      if (
        checkCharacterValidation(
          characterLength.topContent,
          allContents?.firstSectionText?.length,
          "First Section Content"
        ) &&
        checkCharacterValidation(
          characterLength.subContent,
          allContents?.secondSectionText?.length,
          "Second Section Content"
        ) &&
        checkImageValidation(allImages?.bannerImage, "Banner Image") &&
        checkImageValidation(allImages?.firstImage, "First Section Image") &&
        checkImageValidation(
          allImages?.secondSectionImage,
          "Second Section Image"
        )
      ) {
        let formData = new FormData();
        {
          itemData?.id && formData.append("id", itemData?.id);
        }

        formData.append(
          "section_first_description",
          allContents?.firstSectionText?.text
        );
        formData.append(
          "section_second_description",
          allContents?.secondSectionText?.text
        );

        if (typeof allImages?.bannerImage == "object") {
          formData.append(
            "banner_image",
            allImages?.bannerImage,
            allImages?.bannerImage?.name
          );
        }
        if (typeof allImages?.firstImage == "object") {
          formData.append(
            "first_image",
            allImages?.firstImage,
            allImages?.firstImage?.name
          );
        }
        if (typeof allImages?.secondSectionImage == "object") {
          formData.append(
            "second_image",
            allImages?.secondSectionImage,
            allImages?.secondSectionImage?.name
          );
        }

        if (itemData?.id) {
          await FleetManagementServices.updateLastMile(formData);
        } else {
          await FleetManagementServices.createLastMile(formData);
        }
        setLoader(true);
      }
    } catch (error) {
      ErrorToaster(error);
      console.log("🚀 ~ handleDataSubmission ~ error:", error);
    }
  };

  const handlePtnCardSubmission = async (data, id) => {
    try {
      if (id) {
        await FleetManagementServices.updateFleetCard(data);
      } else {
        await FleetManagementServices.createFleetCard(data);
      }
      SuccessToaster("Success");
      setLoader(true);
    } catch (error) {
      console.log("🚀 ~ handlePtnCardSubmission ~ error:", error);
      ErrorToaster("Something went wrong.");
    }
  };

  const handleDeleteItem = async (id) => {
    await FleetManagementServices.deleteFleetCard({ id });
    setLoader(true);
  };

  const getItemData = async () => {
    let res = await FleetManagementServices.getLastMile();
    let ptnCards = await FleetManagementServices.getFleetCard();
    let data = res?.data[0];
    setItemData(data);
    setPtnCardList(ptnCards?.data);
    setAllContents({
      firstSectionText: {
        text: data?.section_first_description,
        length: data?.section_first_description?.length,
      },
      secondSectionText: {
        text: data?.section_second_description,
        length: data?.section_second_description?.length,
      },
    });
    setAllImages({
      bannerImage: data?.banner_image,
      firstImage: data?.first_image,
      secondSectionImage: data?.second_image,
    });
    setLoader(false);
  };

  useEffect(() => {
    if (loader) {
      getItemData();
    }
  }, [loader]);

  return (
    <Layout>
      <WhyPTNDialog
        handleClose={() => {
          setShowDialog(false);
          setSelectedItem({});
        }}
        open={showDialog}
        characterLimit={106}
        selectedItem={selectedItem}
        handleSubmitFunc={(e, id) => handlePtnCardSubmission(e, id)}
      />
      <Box
        sx={{ backgroundColor: colors.lightGray, p: 3, borderRadius: "12px" }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ bgcolor: colors.white, p: 2, borderRadius: "12px" }}
        >
          <Grid item xs={6}>
            <Typography variant="body1" fontWeight="bold" color="initial">
              Fleet Management
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <BreadCrumbs currentPage="Fleet Management" />
          </Grid>
        </Grid>
        {loader ? (
          <LoadingCircle />
        ) : (
          <Box sx={{ mt: 2 }}>
            <ImageEditorCombine
              title={"Banner Image"}
              handleData={(e) => setAllImages({ ...allImages, bannerImage: e })}
              uploadedImage={allImages?.bannerImage}
            />
            <TextEditorCombine
              title="First Secion Text"
              characterLimit={characterLength.topContent}
              data={itemData?.section_first_description}
              handleData={(e, contentLength) =>
                setAllContents({
                  ...allContents,
                  firstSectionText: {
                    text: e,
                    length: contentLength,
                  },
                })
              }
            />
            <TextEditorCombine
              title="Second Secion Text"
              characterLimit={characterLength.subContent}
              data={itemData?.section_second_description}
              handleData={(e, contentLength) =>
                setAllContents({
                  ...allContents,
                  secondSectionText: {
                    text: e,
                    length: contentLength,
                  },
                })
              }
            />
            <ImageEditorCombine
              title={"First Image"}
              handleData={(e) => setAllImages({ ...allImages, firstImage: e })}
              uploadedImage={allImages?.firstImage}
            />
            <ImageEditorCombine
              title={"Second Image"}
              handleData={(e) =>
                setAllImages({ ...allImages, secondSectionImage: e })
              }
              uploadedImage={allImages?.secondSectionImage}
            />
            <PrimaryButton
              title="Save"
              onClick={() => handleDataSubmission()}
              style={{
                margin: "10px 0px",
              }}
            />
            <Accordion
              sx={{
                mb: 1,
                boxShadow: "none",
                "&::before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body1" fontWeight="bold" color="initial">
                  Fleet Cards
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    p: 2,
                  }}
                >
                  <SecondaryButton
                    onClick={() => setShowDialog(!showDialog)}
                    title="Add New Item"
                  />
                </Box>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow sx={{ bgcolor: colors.primary }}>
                        <TableCell
                          sx={{ fontWeight: "bold", color: colors.white }}
                        >
                          Image
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold", color: colors.white }}
                        >
                          Title
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold", color: colors.white }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ptnCardList?.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            <Typography
                              component={"img"}
                              sx={{
                                width: 70,
                                height: 70,
                                objectFit: "contain",
                                borderRadius: 70,
                              }}
                              src={row?.image}
                            />
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {row.title}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleDeleteItem(row?.id)}
                            >
                              <DeleteOutlineTwoTone color="error" />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setSelectedItem(row);
                                setShowDialog(true);
                              }}
                            >
                              <EditTwoTone color="primary" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Box>
    </Layout>
  );
}

export default FleetManagement;
